import maplibregl from 'maplibre-gl';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../../../store/store';
import { JWTHeader } from '../../../../../utils/JWTHeader';
import { getPolygonCentroid, lambert93ToWGS84 } from '../../../../../utils/mapUtils';
import Buildings3D from '../../../../MapLibre/Buildings3D';
import { MapLibreProvider, useMapLibre, useOptionalMapLibre } from '../../../../MapLibre/MapLibreContext';
import OverzoomTileLayer from '../../../../MapLibre/OverzoomTileLayer';
import { SunlightProvider } from '../../../../MapLibre/SunlightContext';
import * as styles from './CoproDetailsMap.module.css';

interface ParcelleInput {
  plot: string;
}

interface AddressInput {
  remoteId: string;
}

interface CoproDetailsMapProps {
  parcelles: ParcelleInput[];
  addresses: AddressInput[];
  initialCenter?: [number, number];
  initialZoom?: number;
}

interface WgsPolygon extends GeoJSON.Polygon {
  coordinates: WgsCoordinate[][];
}

interface WgsMultiPolygon extends GeoJSON.MultiPolygon {
  coordinates: WgsCoordinate[][][];
}

type ParcelleGeometry = WgsPolygon | WgsMultiPolygon;

interface Parcelle { id: string; surfaceParcelle: number; geometry: ParcelleGeometry }
interface Address { id: string; geometry: { type: string; coordinates: [number, number] }; numero: string; rep: string | null; nomVoie: string; nomCom: string; postalCode: string }

const SELECTED_FILTER_FALLBACK = '__none__';

type WgsCoordinate = [number, number]; // [lng, lat]

interface MapTarget {
  key: string;
  lng: number;
  lat: number;
  bounds?: [[number, number], [number, number]];
}

const isCoordinatePair = (value: unknown): value is [number, number] => Array.isArray(value) && value.length === 2 && value.every((v) => typeof v === 'number' && Number.isFinite(v));

const toWgsCoordinate = (pair: [number, number]): WgsCoordinate => {
  const [x, y] = pair;
  if (Math.abs(x) > 180 || Math.abs(y) > 90) {
    const { lat, lng } = lambert93ToWGS84(x, y);
    return [lng, lat];
  }
  return [x, y];
};

const convertGeometryToWgs84 = (geometry: unknown): ParcelleGeometry | null => {
  if (!geometry || typeof geometry !== 'object' || !(('type' in geometry) && ('coordinates' in geometry))) {
    return null;
  }

  const convertRing = (ring: unknown): WgsCoordinate[] => {
    if (!Array.isArray(ring)) {
      return [];
    }
    const result: WgsCoordinate[] = [];
    ring.forEach((coord) => {
      if (isCoordinatePair(coord)) {
        result.push(toWgsCoordinate(coord));
      } else if (Array.isArray(coord) && coord.length >= 2) {
        const [x, y] = coord;
        if (typeof x === 'number' && typeof y === 'number') {
          result.push(toWgsCoordinate([x, y]));
        }
      }
    });
    return result;
  };

  const geom = geometry as { type: string; coordinates: unknown };

  if (geom.type === 'Polygon') {
    const coordinates = Array.isArray(geom.coordinates)
      ? (geom.coordinates as unknown[]).map((ring) => convertRing(ring))
      : [];
    return { type: 'Polygon', coordinates: coordinates as WgsCoordinate[][] };
  }

  if (geom.type === 'MultiPolygon') {
    const coordinates = Array.isArray(geom.coordinates)
      ? (geom.coordinates as unknown[]).map((polygon) => {
        if (!Array.isArray(polygon)) { return []; }
        return polygon.map((ring) => convertRing(ring));
      })
      : [];
    return { type: 'MultiPolygon', coordinates: coordinates as WgsCoordinate[][][] };
  }

  return null;
};

const collectGeometryCoordinates = (geometry: ParcelleGeometry): WgsCoordinate[] => {
  const coordinates: WgsCoordinate[] = [];
  const pushCoords = (coords: unknown) => {
    if (isCoordinatePair(coords)) {
      coordinates.push([coords[0], coords[1]]);
      return;
    }
    if (Array.isArray(coords)) {
      coords.forEach((c) => pushCoords(c));
    }
  };
  pushCoords(geometry.coordinates);
  return coordinates;
};

const computeGeometryBounds = (geometry: ParcelleGeometry): [[number, number], [number, number]] | null => {
  const coords = collectGeometryCoordinates(geometry);
  if (coords.length === 0) { return null; }
  let minLng = Infinity; let minLat = Infinity; let maxLng = -Infinity; let maxLat = -Infinity;
  coords.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  });
  if (!Number.isFinite(minLng) || !Number.isFinite(minLat) || !Number.isFinite(maxLng) || !Number.isFinite(maxLat)) {
    return null;
  }
  return [[minLng, minLat], [maxLng, maxLat]];
};

const getParcelleOuterRing = (geometry: ParcelleGeometry): WgsCoordinate[] => {
  const normalizeRing = (ring: unknown): WgsCoordinate[] => {
    if (!Array.isArray(ring)) {
      return [];
    }
    const output: WgsCoordinate[] = [];
    for (const coord of ring) {
      if (isCoordinatePair(coord)) {
        const [lng, lat] = coord;
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          output.push([lng, lat]);
        }
      } else if (Array.isArray(coord) && coord.length >= 2) {
        const [lng, lat] = coord as [number, number];
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          output.push([lng, lat]);
        }
      }
    }
    return output;
  };

  if (geometry.type === 'Polygon') {
    return normalizeRing(geometry.coordinates[0]);
  }
  const firstPolygon = geometry.coordinates[0];
  const firstRing = Array.isArray(firstPolygon) ? firstPolygon[0] : null;
  return normalizeRing(firstRing);
};

const computeParcelleCentroid = (geometry: ParcelleGeometry): { lat: number; lng: number } | null => {
  const ring = getParcelleOuterRing(geometry);
  if (!ring || ring.length === 0) {
    return null;
  }
  const latLngRing = ring.map(([lng, lat]) => [lat, lng]);
  const centroid = getPolygonCentroid(latLngRing);
  const { lat, lng } = centroid;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  return { lat, lng };
};

function ParcelsLayer({ parcelles, selectedParcelleId, onClick }: { parcelles: Parcelle[]; selectedParcelleId: string | null; onClick: (p: Parcelle) => void }) {
  const map = useMapLibre();
  const sourceId = 'copro-parcels';
  const layerId = 'copro-parcels-fill';
  const clickLayerId = 'copro-parcels-outline';
  const selectedFillId = 'copro-parcels-selected-fill';
  const selectedOutlineId = 'copro-parcels-selected-outline';
  const geojson = useMemo<GeoJSON.FeatureCollection<ParcelleGeometry>>(() => ({
    type: 'FeatureCollection',
    features: parcelles.map((p) => ({ type: 'Feature', properties: { id: p.id, surfaceParcelle: p.surfaceParcelle }, geometry: p.geometry })),
  }), [parcelles]);

  useEffect(() => {
    // If the map has been removed or style isn't ready, skip safely
    if (!map || typeof (map as maplibregl.Map).isStyleLoaded !== 'function' || !(map as maplibregl.Map).isStyleLoaded()) {
      return undefined;
    }

    try {
      if (map.getLayer(selectedOutlineId)) { map.removeLayer(selectedOutlineId); }
      if (map.getLayer(selectedFillId)) { map.removeLayer(selectedFillId); }
      if (map.getLayer(clickLayerId)) { map.removeLayer(clickLayerId); }
      if (map.getLayer(layerId)) { map.removeLayer(layerId); }
      if (map.getSource(sourceId)) { map.removeSource(sourceId); }
      
      map.addSource(sourceId, { type: 'geojson', data: geojson } as maplibregl.GeoJSONSourceSpecification);
      
      // Get the buildings layer to insert our layers before it
      const buildingsLayerId = 'buildings-3d-threejs';
      const beforeLayer = map.getLayer(buildingsLayerId) ? buildingsLayerId : undefined;
      
      map.addLayer({ 
        id: layerId, 
        type: 'fill', 
        source: sourceId, 
        paint: { 'fill-color': '#1d4ed8', 'fill-opacity': 0.25 }, 
      } as maplibregl.LayerSpecification, beforeLayer);
      
      map.addLayer({ 
        id: clickLayerId, 
        type: 'line', 
        source: sourceId, 
        paint: { 'line-color': '#1d4ed8', 'line-width': 2 }, 
      } as maplibregl.LayerSpecification, beforeLayer);
      
      map.addLayer({
        id: selectedFillId,
        type: 'fill',
        source: sourceId,
        paint: { 'fill-color': '#1d4ed8', 'fill-opacity': 0.45 },
        filter: ['==', ['get', 'id'], selectedParcelleId ?? SELECTED_FILTER_FALLBACK],
      } as maplibregl.LayerSpecification, beforeLayer);
      
      map.addLayer({
        id: selectedOutlineId,
        type: 'line',
        source: sourceId,
        paint: { 'line-color': '#0f172a', 'line-width': 2.5 },
        filter: ['==', ['get', 'id'], selectedParcelleId ?? SELECTED_FILTER_FALLBACK],
      } as maplibregl.LayerSpecification, beforeLayer);
    } catch {
      // Ignore runtime errors caused by rapid unmount/navigation
      // console.warn('ParcelsLayer add error', error);
    }

    const onMapClick = (e: maplibregl.MapLayerMouseEvent) => {
      // Guard against map/style being unavailable during navigation
      if (!map || !map.isStyleLoaded()) { return; }
      const features = map.queryRenderedFeatures(e.point, { layers: [clickLayerId] });
      if (features.length > 0) {
        const id = (features[0].properties?.id as string) ?? null;
        if (id) {
          const p = parcelles.find((pp) => pp.id === id);
          if (p) { onClick(p); }
        }
      }
    };
    try { map.on('click', onMapClick); } catch {}
    return () => {
      try { map.off('click', onMapClick); } catch {}
      if (!map || (typeof (map as maplibregl.Map).isStyleLoaded === 'function' && (map as maplibregl.Map).isStyleLoaded() === false)) { return; }
      try { if (map.getLayer(selectedOutlineId)) { map.removeLayer(selectedOutlineId); } } catch {}
      try { if (map.getLayer(selectedFillId)) { map.removeLayer(selectedFillId); } } catch {}
      try { if (map.getLayer(layerId)) { map.removeLayer(layerId); } } catch {}
      try { if (map.getLayer(clickLayerId)) { map.removeLayer(clickLayerId); } } catch {}
      try { if (map.getSource(sourceId)) { map.removeSource(sourceId); } } catch {}
    };
  }, [map, geojson, layerId, clickLayerId, sourceId, selectedFillId, selectedOutlineId, parcelles, onClick, selectedParcelleId]);

  useEffect(() => {
    if (!map) { return; }
    const filter = ['==', ['get', 'id'], selectedParcelleId ?? SELECTED_FILTER_FALLBACK] as maplibregl.ExpressionSpecification;
    try {
      if (map.getLayer(selectedFillId)) { map.setFilter(selectedFillId, filter); }
      if (map.getLayer(selectedOutlineId)) { map.setFilter(selectedOutlineId, filter); }
    } catch {
      // ignore filter update errors
    }
  }, [map, selectedParcelleId, selectedFillId, selectedOutlineId]);

  return null;
}

function ZoomListener({ onZoom }: { onZoom: (zoom: number) => void }) {
  const map = useMapLibre();

  useEffect(() => {
    const update = () => {
      try {
        onZoom(map.getZoom());
      } catch {
        // ignore zoom errors
      }
    };
    update();
    map.on('zoom', update);
    map.on('zoomend', update);
    return () => {
      map.off('zoom', update);
      map.off('zoomend', update);
    };
  }, [map, onZoom]);

  return null;
}

function SelectedLocationOverlay({
  selectedParcelle,
  selectedParcelleCentroid,
  fallbackAddress,
}: {
  selectedParcelle: Parcelle | null;
  selectedParcelleCentroid: { lat: number; lng: number } | null;
  fallbackAddress: Address | null;
}) {
  const map = useOptionalMapLibre();
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const lastTargetRef = useRef<string | null>(null);

  const fallbackTarget = useMemo(() => {
    if (!fallbackAddress) {
      return null;
    }
    const [lng, lat] = fallbackAddress.geometry.coordinates;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return null;
    }
    return { key: `address:${fallbackAddress.id}`, lng, lat };
  }, [fallbackAddress]);

  useEffect(() => {
    if (!map) { return; }

    const parcelBounds = selectedParcelle ? computeGeometryBounds(selectedParcelle.geometry) : null;
    const parcelTarget: MapTarget | null = selectedParcelle && selectedParcelleCentroid
      ? {
          key: `parcelle:${selectedParcelle.id}`,
          lng: selectedParcelleCentroid.lng,
          lat: selectedParcelleCentroid.lat,
          bounds: parcelBounds ?? undefined,
        }
      : null;

    const target: MapTarget | null = parcelTarget ?? fallbackTarget;

    if (!target) {
      if (markerRef.current) {
        try { markerRef.current.remove(); } catch {}
        markerRef.current = null;
      }
      lastTargetRef.current = null;
      return;
    }

    if (!markerRef.current) {
      markerRef.current = new maplibregl.Marker({ color: '#0284c7', anchor: 'bottom' });
    }

    try {
      markerRef.current.setLngLat([target.lng, target.lat]).addTo(map);
    } catch {
      // ignore marker errors
    }

    if (lastTargetRef.current === target.key) {
      return;
    }

    lastTargetRef.current = target.key;

    try {
      if (target.bounds) {
        map.fitBounds(target.bounds, { padding: 80, duration: 800, maxZoom: 18 });
      } else {
        map.flyTo({ center: [target.lng, target.lat], zoom: 18, pitch: 45, duration: 800, essential: true });
      }
    } catch (error) {
      console.warn('CoproDetailsMap navigation error:', error);
    }
  }, [fallbackTarget, map, selectedParcelle, selectedParcelleCentroid]);

  useEffect(() => () => {
    if (markerRef.current) {
      try { markerRef.current.remove(); } catch {}
      markerRef.current = null;
    }
  }, []);

  return null;
}

const CoproDetailsMap: React.FC<CoproDetailsMapProps> = ({ parcelles: initialParcelles, addresses: initialAddresses, initialCenter = [46.227638, 2.213749], initialZoom = 5 }) => {
  const [mapCenter, setMapCenter] = useState<[number, number]>(initialCenter);
  const [mapZoom, setMapZoom] = useState(initialZoom);
  const [parcelles, setParcelles] = useState<Parcelle[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedParcelle, setSelectedParcelle] = useState<Parcelle | null>(null);
  const [currentZoom, setCurrentZoom] = useState(initialZoom);
  const jwt = useSelector((state: RootState) => state.auth.token);

  useEffect(() => {
    const fetchParcelleDetails = async(parcelleId: string) => {
      try {
        const response = await fetch(`/api/cadastre/plots/${parcelleId}`, JWTHeader(jwt));
        if (!response.ok) {throw new Error('Failed to fetch parcelle');}
        const data = await response.json();
        const geometryRaw = JSON.parse(data.geometry);
        const geometry = convertGeometryToWgs84(geometryRaw);
        if (!geometry) {
          throw new Error('Unsupported parcelle geometry');
        }
        return { id: data.id, surfaceParcelle: data.surfaceParcelle, geometry } as Parcelle;
      } catch (error) { console.error('Error fetching parcelle details :', error); return null; }
    };

    const fetchAddressDetails = async(addressId: string) => {
      try {
        const response = await fetch(`/api/ban/${addressId}`, JWTHeader(jwt));
        if (!response.ok) {throw new Error('Failed to fetch address');}
        const data = await response.json();
        const geometry = JSON.parse(data.geometry);
        const { lat, lng } = lambert93ToWGS84(geometry.coordinates[0], geometry.coordinates[1]);
        return { id: data.id, geometry: { type: 'Point', coordinates: [lng, lat] } as Address['geometry'], numero: data.numero, rep: data.rep, nomVoie: data.nomVoie, nomCom: data.nomCom, postalCode: data.postalCode } as Address;
      } catch (error) { console.error('Error fetching address details :', error); return null; }
    };

    const fetchAllDetails = async() => {
      const parcellePromises = initialParcelles.map((p) => fetchParcelleDetails(p.plot));
      const addressPromises = initialAddresses.map((a) => fetchAddressDetails(a.remoteId));
      const fetchedParcelles = (await Promise.all(parcellePromises)).filter((p) => p !== null) as Parcelle[];
      const fetchedAddresses = (await Promise.all(addressPromises)).filter((a) => a !== null) as Address[];
      setParcelles(fetchedParcelles);
      setAddresses(fetchedAddresses);
    };

    fetchAllDetails();
  }, [initialParcelles, initialAddresses, jwt]);

  useEffect(() => {
    if (parcelles.length > 0) {
      const centroid = computeParcelleCentroid(parcelles[0].geometry);
      if (centroid) {
        setMapCenter([centroid.lat, centroid.lng]);
        setMapZoom(18);
        return;
      }
    }
    if (addresses.length > 0) {
      const [lng, lat] = addresses[0].geometry.coordinates;
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        setMapCenter([lat, lng]);
        setMapZoom(17);
      }
    }
  }, [parcelles, addresses]);

  const handleParcelleClick = (parcelle: Parcelle) => { setSelectedParcelle(parcelle); };
  const selectedParcelleCentroid = useMemo(() => (selectedParcelle ? computeParcelleCentroid(selectedParcelle.geometry) : null), [selectedParcelle]);
  const fallbackAddress = useMemo(() => (addresses.length > 0 ? addresses[0] : null), [addresses]);
  const is3DEnabled = currentZoom >= 15;
  const selectedHighlightAreas = useMemo<GeoJSON.FeatureCollection<ParcelleGeometry>>(() => {
    if (!selectedParcelle) {
      return { type: 'FeatureCollection', features: [] };
    }
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: selectedParcelle.geometry }],
    };
  }, [selectedParcelle]);

  return (
    <SunlightProvider>
    <MapLibreProvider center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }} className={styles.mapContainer} jwt={jwt}>
        <ZoomListener onZoom={setCurrentZoom} />
      <OverzoomTileLayer isCadastre={false} />
        <Buildings3D enabled={is3DEnabled} highlightAreas={selectedHighlightAreas.features.length > 0 ? selectedHighlightAreas : undefined} highlightColor="#38bdf8" defaultColor="#d1d5db" />
        <ParcelsLayer parcelles={parcelles} selectedParcelleId={selectedParcelle?.id ?? null} onClick={handleParcelleClick} />
        <SelectedLocationOverlay selectedParcelle={selectedParcelle} selectedParcelleCentroid={selectedParcelleCentroid} fallbackAddress={fallbackAddress} />
      {/* TODO: port address markers and popup UI to MapLibre if needed */}
    </MapLibreProvider>
    </SunlightProvider>
  );
};

export default CoproDetailsMap;
